import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Edit, Save, X, FileText, Plus, Trash2, Eye } from 'lucide-react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import api from '../../utils/api';

const quillModules = {
  toolbar: [
    [{ 'header': [1, 2, 3, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ 'color': [] }, { 'background': [] }],
    [{ 'list': 'ordered'}, { 'list': 'bullet' }],
    [{ 'align': [] }],
    ['link'],
    ['clean']
  ],
};

const quillFormats = [
  'header',
  'bold', 'italic', 'underline', 'strike',
  'color', 'background',
  'list', 'bullet',
  'align',
  'link'
];

const PagesManagement = () => {
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [showNewPageModal, setShowNewPageModal] = useState(false);
  const [newPage, setNewPage] = useState({
    slug: '',
    title_tr: '',
    title_en: '',
    content_tr: '',
    content_en: ''
  });

  const fetchPages = useCallback(async () => {
    try {
      const response = await api.get('/pages');
      setPages(response.data);
    } catch (error) {
      console.error('Error fetching pages:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPages();
  }, [fetchPages]);

  const startEdit = (page) => {
    setEditingId(page.id);
    setEditForm({ ...page });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditForm({});
  };

  const saveEdit = async () => {
    setSaving(true);
    try {
      await api.put(`/pages/${editingId}`, editForm);
      await fetchPages();
      setEditingId(null);
      setEditForm({});
      alert('Sayfa başarıyla güncellendi!');
    } catch (error) {
      console.error('Error updating page:', error);
      alert('Sayfa güncellenirken hata oluştu');
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setEditForm(prev => ({ ...prev, [name]: value }));
  };

  const handleNewPageChange = (e) => {
    const { name, value } = e.target;
    setNewPage(prev => ({ ...prev, [name]: value }));
  };

  const createPage = async () => {
    if (!newPage.slug || !newPage.title_tr) {
      alert('Slug ve Türkçe başlık zorunludur');
      return;
    }
    
    setSaving(true);
    try {
      await api.post('/pages', newPage);
      await fetchPages();
      setShowNewPageModal(false);
      setNewPage({ slug: '', title_tr: '', title_en: '', content_tr: '', content_en: '' });
      alert('Sayfa başarıyla oluşturuldu!');
    } catch (error) {
      console.error('Error creating page:', error);
      alert('Sayfa oluşturulurken hata oluştu');
    } finally {
      setSaving(false);
    }
  };

  const deletePage = async (id, title) => {
    if (!window.confirm(`"${title}" sayfasını silmek istediğinize emin misiniz?`)) return;
    
    try {
      await api.delete(`/pages/${id}`);
      await fetchPages();
      alert('Sayfa silindi!');
    } catch (error) {
      console.error('Error deleting page:', error);
      alert('Sayfa silinirken hata oluştu');
    }
  };

  return (
    <div data-testid="admin-pages">
      {/* Header */}
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-3xl font-bold text-bms-text mb-2">Sayfalar</h1>
          <p className="text-bms-text-muted">Sayfa içeriklerini görsel editör ile düzenleyin</p>
        </div>
        <button
          onClick={() => setShowNewPageModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-bms-primary hover:bg-bms-primary-dark text-white rounded-lg transition-all"
        >
          <Plus className="w-5 h-5" />
          Yeni Sayfa
        </button>
      </div>

      {loading ? (
        <div className="text-bms-text-muted">Yükleniyor...</div>
      ) : pages.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-xl border border-gray-100 shadow-corporate">
          <FileText className="w-12 h-12 text-bms-text-muted mx-auto mb-4" />
          <p className="text-bms-text-muted">Henüz sayfa eklenmemiş</p>
        </div>
      ) : (
        <div className="space-y-4">
          {pages.map((page, index) => {
            const isEditing = editingId === page.id;

            return (
              <motion.div
                key={page.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className="bg-white rounded-xl border border-gray-100 shadow-corporate overflow-hidden"
              >
                {isEditing ? (
                  <div className="p-6 space-y-6">
                    {/* Slug */}
                    <div>
                      <label className="block text-sm font-medium text-bms-text mb-2">Slug (URL)</label>
                      <input
                        type="text"
                        value={editForm.slug}
                        disabled
                        className="w-full px-4 py-3 rounded-lg border border-gray-200 bg-gray-50 text-bms-text-muted"
                      />
                      <p className="text-xs text-bms-text-muted mt-1">URL: /{editForm.slug}</p>
                    </div>

                    {/* Titles */}
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-bms-text mb-2">Başlık (TR) *</label>
                        <input
                          type="text"
                          name="title_tr"
                          value={editForm.title_tr || ''}
                          onChange={handleChange}
                          className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-bms-primary focus:ring-2 focus:ring-bms-primary/20 outline-none transition-all"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-bms-text mb-2">Title (EN)</label>
                        <input
                          type="text"
                          name="title_en"
                          value={editForm.title_en || ''}
                          onChange={handleChange}
                          className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-bms-primary focus:ring-2 focus:ring-bms-primary/20 outline-none transition-all"
                        />
                      </div>
                    </div>

                    {/* Content TR - Rich Editor */}
                    <div>
                      <label className="block text-sm font-medium text-bms-text mb-2">İçerik (TR)</label>
                      <div className="border border-gray-200 rounded-lg overflow-hidden">
                        <ReactQuill
                          theme="snow"
                          value={editForm.content_tr || ''}
                          onChange={(value) => setEditForm(prev => ({ ...prev, content_tr: value }))}
                          modules={quillModules}
                          formats={quillFormats}
                          style={{ minHeight: '200px' }}
                        />
                      </div>
                    </div>

                    {/* Content EN - Rich Editor */}
                    <div>
                      <label className="block text-sm font-medium text-bms-text mb-2">Content (EN)</label>
                      <div className="border border-gray-200 rounded-lg overflow-hidden">
                        <ReactQuill
                          theme="snow"
                          value={editForm.content_en || ''}
                          onChange={(value) => setEditForm(prev => ({ ...prev, content_en: value }))}
                          modules={quillModules}
                          formats={quillFormats}
                          style={{ minHeight: '200px' }}
                        />
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
                      <button
                        onClick={cancelEdit}
                        className="px-4 py-2 rounded-lg border border-gray-200 text-bms-text-light hover:bg-gray-50 flex items-center gap-2 transition-all"
                      >
                        <X className="w-4 h-4" /> İptal
                      </button>
                      <button
                        onClick={saveEdit}
                        disabled={saving}
                        className="px-4 py-2 rounded-lg bg-bms-primary hover:bg-bms-primary-dark text-white flex items-center gap-2 transition-all disabled:opacity-50"
                      >
                        <Save className="w-4 h-4" /> {saving ? 'Kaydediliyor...' : 'Kaydet'}
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center gap-4 p-4">
                    <div className="w-12 h-12 rounded-xl bg-blue-50 flex items-center justify-center flex-shrink-0">
                      <FileText className="w-6 h-6 text-bms-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-bms-text truncate">{page.title_tr}</h3>
                      <p className="text-sm text-bms-text-muted">/{page.slug}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <a
                        href={`/${page.slug}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                        title="Sayfayı Görüntüle"
                      >
                        <Eye className="w-5 h-5 text-green-600" />
                      </a>
                      <button
                        onClick={() => startEdit(page)}
                        className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                        title="Düzenle"
                      >
                        <Edit className="w-5 h-5 text-bms-primary" />
                      </button>
                      <button
                        onClick={() => deletePage(page.id, page.title_tr)}
                        className="p-2 rounded-lg hover:bg-red-50 transition-colors"
                        title="Sil"
                      >
                        <Trash2 className="w-5 h-5 text-red-500" />
                      </button>
                    </div>
                  </div>
                )}
              </motion.div>
            );
          })}
        </div>
      )}

      {/* New Page Modal */}
      {showNewPageModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-xl shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto"
          >
            <div className="p-6 border-b border-gray-100">
              <h2 className="text-xl font-bold text-bms-text">Yeni Sayfa Oluştur</h2>
            </div>
            
            <div className="p-6 space-y-6">
              {/* Slug */}
              <div>
                <label className="block text-sm font-medium text-bms-text mb-2">Slug (URL) *</label>
                <input
                  type="text"
                  name="slug"
                  value={newPage.slug}
                  onChange={handleNewPageChange}
                  className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-bms-primary focus:ring-2 focus:ring-bms-primary/20 outline-none transition-all"
                  placeholder="ornek-sayfa"
                />
              </div>

              {/* Titles */}
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-bms-text mb-2">Başlık (TR) *</label>
                  <input
                    type="text"
                    name="title_tr"
                    value={newPage.title_tr}
                    onChange={handleNewPageChange}
                    className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-bms-primary focus:ring-2 focus:ring-bms-primary/20 outline-none transition-all"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-bms-text mb-2">Title (EN)</label>
                  <input
                    type="text"
                    name="title_en"
                    value={newPage.title_en}
                    onChange={handleNewPageChange}
                    className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-bms-primary focus:ring-2 focus:ring-bms-primary/20 outline-none transition-all"
                  />
                </div>
              </div>

              {/* Content TR */}
              <div>
                <label className="block text-sm font-medium text-bms-text mb-2">İçerik (TR)</label>
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <ReactQuill
                    theme="snow"
                    value={newPage.content_tr}
                    onChange={(value) => setNewPage(prev => ({ ...prev, content_tr: value }))}
                    modules={quillModules}
                    formats={quillFormats}
                    style={{ minHeight: '150px' }}
                  />
                </div>
              </div>

              {/* Content EN */}
              <div>
                <label className="block text-sm font-medium text-bms-text mb-2">Content (EN)</label>
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <ReactQuill
                    theme="snow"
                    value={newPage.content_en}
                    onChange={(value) => setNewPage(prev => ({ ...prev, content_en: value }))}
                    modules={quillModules}
                    formats={quillFormats}
                    style={{ minHeight: '150px' }}
                  />
                </div>
              </div>
            </div>

            <div className="p-6 border-t border-gray-100 flex justify-end gap-3">
              <button
                onClick={() => setShowNewPageModal(false)}
                className="px-4 py-2 rounded-lg border border-gray-200 text-bms-text-light hover:bg-gray-50 transition-all"
              >
                İptal
              </button>
              <button
                onClick={createPage}
                disabled={saving}
                className="px-4 py-2 rounded-lg bg-bms-primary hover:bg-bms-primary-dark text-white transition-all disabled:opacity-50"
              >
                {saving ? 'Oluşturuluyor...' : 'Oluştur'}
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Quill CSS Override */}
      <style>{`
        .ql-container {
          font-size: 16px;
          min-height: 150px;
        }
        .ql-editor {
          min-height: 150px;
        }
        .ql-toolbar {
          border-color: #e2e8f0 !important;
          background: #f8fafc;
        }
        .ql-container {
          border-color: #e2e8f0 !important;
        }
      `}</style>
    </div>
  );
};

export default PagesManagement;
